// src/pages/Login.jsx
//
// The lobby. Two-column asymmetric layout (≥ md). On mobile, the rail is
// hidden — only the form shows. On mount, we probe /api/lockout so that a
// returning locked-out user sees the closed state instead of the form.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LobbyRail from "../components/LobbyRail.jsx";
import SignInForm from "../components/SignInForm.jsx";
import LockedState from "../components/LockedState.jsx";
import { api } from "../lib/api.js";

export default function Login() {
  const navigate = useNavigate();
  const [boot, setBoot] = useState({
    locked: false,
    lockoutUntil: null,
    remainingAttempts: 3,
    lastAttemptAt: null,
  });
  const [cooldownMin, setCooldownMin] = useState(15);
  const [maxAttempts, setMaxAttempts] = useState(3);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If we already have a valid session, go straight to dashboard.
      const me = await api("/me", { method: "GET" });
      if (!cancelled && me.ok && me.body?.ok) {
        navigate("/dashboard", { replace: true });
        return;
      }
      // Otherwise, ask /api/lockout for current state.
      const lo = await api("/lockout", { method: "GET" });
      if (cancelled) return;
      if (lo.ok && lo.body) {
        setBoot({
          locked: !!lo.body.locked,
          lockoutUntil: lo.body.lockoutUntil ?? null,
          remainingAttempts: lo.body.remainingAttempts ?? 3,
          lastAttemptAt: null,
        });
        // The server doesn't echo config (yet) — we infer cooldown from
        // the lockout window when locked, else fall back to 15min default.
        if (lo.body.lockoutUntil) {
          const mins = Math.max(
            1,
            Math.round((lo.body.lockoutUntil - Date.now()) / 60000),
          );
          setCooldownMin(mins);
        }
        setMaxAttempts(Math.max(1, lo.body.maxAttempts ?? 3));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <main className="min-h-screen flex bg-aught-paper paper-tex">
      <LobbyRail
        failedCount={Math.max(0, maxAttempts - (boot.remainingAttempts ?? maxAttempts))}
        maxAttempts={maxAttempts}
        lastAttemptAt={boot.lastAttemptAt}
        lockedUntil={boot.lockoutUntil}
      />
      <section className="flex-1 flex items-center justify-center px-6 sm:px-10 py-16 md:py-24">
        {boot.locked ? (
          <LockedState
            initialLockoutUntil={boot.lockoutUntil}
            cooldownMin={cooldownMin}
          />
        ) : (
          <SignInForm
            initialRemaining={boot.remainingAttempts ?? maxAttempts}
            initialLastAttemptAt={boot.lastAttemptAt}
            cooldownMin={cooldownMin}
          />
        )}
      </section>
    </main>
  );
}
